import { Mail, ArrowRight, Linkedin } from "lucide-react";
import { Button } from "./Button";
import { Input } from "@/components/ui/input";
import { Link } from "react-router-dom";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { CookieSettingsButton } from "@/components/cookies/CookieSettingsButton";

export const FooterSection = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: "Error",
        description: "Please enter your email address",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    console.log("Submitting newsletter signup:", email);

    try {
      const response = await fetch("https://hook.us2.make.com/gxwma5mw1n2ubuen50yaddouxfbjvjkv", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        mode: "no-cors",
        body: JSON.stringify({
          email: email,
          timestamp: new Date().toISOString(),
          source: "newsletter_signup",
          page: window.location.href,
        }),
      });

      toast({
        title: "Success!",
        description: "Thank you for subscribing to our newsletter!",
      });
      
      setEmail("");
    } catch (error) {
      console.error("Error submitting newsletter signup:", error);
      toast({
        title: "Error",
        description: "Failed to subscribe. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="relative bg-background overflow-hidden">
      {/* Top border gradient */}
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-blue-500/20 to-transparent"></div>
      
      <div className="container mx-auto px-6 py-16 max-w-6xl">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Company Info */}
          <div className="space-y-6">
            <a href="#" className="flex items-center">
              <img 
                src="/lovable-uploads/Docket Labs logo updated (blue - white).png" 
                alt="DocketLabs Logo" 
                className="h-5"
              />
            </a>
            
            <p className="text-white/70">
              Transforming legal workflows with AI excellence. We empower law firms with cutting-edge artificial intelligence solutions.
            </p>
          </div>
          
          {/* Quick Links - Updated order */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white">Quick Links</h3>
            <ul className="space-y-4">
              <li>
                <Link to="/services" className="text-white/70 hover:text-white transition-colors">Services</Link>
              </li>
              <li>
                <Link to="/expertise" className="text-white/70 hover:text-white transition-colors">Expertise</Link>
              </li>
              <li>
                <Link to="/about-us" className="text-white/70 hover:text-white transition-colors">About Us</Link>
              </li>
              <li>
                <Link to="/contact" className="text-white/70 hover:text-white transition-colors">Contact</Link>
              </li>
            </ul>
          </div>
          
          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start">
                <Mail className="h-5 w-5 text-primary mr-3 mt-0.5" />
                <a href="mailto:hello@docketlabs.com" className="text-white/70 hover:text-white transition-colors">
                  hello@docketlabs.com
                </a>
              </li>
              <li className="flex items-start">
                <Linkedin className="h-5 w-5 text-primary mr-3 mt-0.5" />
                <a 
                  href="https://www.linkedin.com/company/docketlabs/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-white/70 hover:text-white transition-colors"
                >
                  Follow us on LinkedIn
                </a>
              </li>
            </ul>
          </div>
          
          {/* Newsletter Signup */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white">Stay Updated</h3>
            <p className="text-white/70 mb-4">
              Subscribe to our newsletter for the latest AI legal insights.
            </p>
            
            <form onSubmit={handleNewsletterSubmit} className="space-y-3">
              <div className="relative">
                <Input 
                  type="email" 
                  placeholder="Email address" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSubmitting}
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/50 focus:border-primary h-11 pr-12"
                />
                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-white/70 hover:text-primary transition-colors disabled:opacity-50"
                  aria-label="Subscribe"
                >
                  <ArrowRight className="h-5 w-5" />
                </button>
              </div>
              
              <div className="text-xs text-white/50">
                We respect your privacy. Unsubscribe at any time.
              </div>
            </form>
          </div>
        </div>
        
        <div className="mt-16 pt-8 border-t border-white/10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-white/50 text-sm text-center sm:text-left">
              © {new Date().getFullYear()} DocketLabs. All rights reserved. <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
            </p>
            <CookieSettingsButton />
          </div>
        </div>
      </div>
    </footer>
  );
};

export default FooterSection;
